import { useState } from "preact/hooks"
import { TransactionJson } from "@/api"
import { createPromotionMatch } from "@/api/deduction.tsx"
import { toast } from "@/components/ui/use-toast.ts"
import { classes } from "@/utils"
import { RefreshFlag } from "@/refresh_flag.tsx"
import {
  PromotionSelector,
  PromotionMatch,
  buildPromotionSearchSeedFromTransaction,
} from "@/accounting_detail/PromotionSelector.tsx"
import { PromotionDisplay } from "@/deduction_detail"

interface TransactionPromotionFieldProps {
  transaction: TransactionJson
  className?: string
}

/**
 * True when the server named a promotion for this transaction — a GL-coding split, the
 * deduction-level `deduction_promotion_match`, or the GoodPop-only `promotion_remote_ids`
 * enrichment. Mirrors what `PromotionDisplay` will actually render, so the caller knows whether it
 * still owes the field a label.
 */
export function transaction_has_promotion_match(
  transaction: Pick<TransactionJson, "promotion_name" | "promotion_remote_id" | "promotion_remote_ids">
): boolean {
  return Boolean(transaction.promotion_name || transaction.promotion_remote_id || transaction.promotion_remote_ids)
}

/**
 * Whether the deduction-level promotion picker should be offered.
 *
 * When the displayed match comes from the GL coding, the picker would be a trap: it writes a
 * `deduction_promotion_match` row, which the split then outranks — so the value would snap back to
 * the split's promotion the moment the page refetched. The split cards below are where that
 * promotion is changed.
 */
export function should_offer_deduction_promotion_picker(
  transaction: Pick<TransactionJson, "promotion_match_source">
): boolean {
  return transaction.promotion_match_source !== "split"
}

/**
 * The deduction's promotion match, on the accounting detail view.
 *
 * The accounting pages used to show only `promotion_remote_ids` — an org-gated enrichment — so a
 * deduction matched to a promotion read as unmatched here even though the deduction detail page
 * and the transactions export both showed it. This is the accounting-side twin of
 * `DeductionPromotionField`: it names the current match and lets the user change it.
 */
export function TransactionPromotionField({ transaction, className }: TransactionPromotionFieldProps) {
  const [saving, setSaving] = useState(false)
  const matched = transaction_has_promotion_match(transaction)
  const offer_picker = should_offer_deduction_promotion_picker(transaction)

  async function handleSelect(match: PromotionMatch) {
    setSaving(true)
    try {
      await createPromotionMatch({
        promotion_id: match.promotion.id,
        deduction_id: transaction.id,
      })
      toast({
        title: "Promotion linked",
        description: match.promotion.name,
      })
      // The transaction is re-fetched by the page, which is what re-renders the name above.
      RefreshFlag.set((f: boolean) => !f)
    } catch (error) {
      toast({
        title: "Failed to link promotion",
        description: error as Error,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div class={classes("group relative", className)}>
      {matched ? (
        <PromotionDisplay
          promotion_id={transaction.promotion_id}
          promotion_name={transaction.promotion_name}
          promotion_remote_id={transaction.promotion_remote_id}
          promotion_remote_ids={transaction.promotion_remote_ids}
          className="mb-1"
        />
      ) : (
        <label class="text-gray-500 text-sm block mb-1">Matched Promotion</label>
      )}
      {offer_picker ? (
        <div class={saving ? "opacity-50 pointer-events-none" : ""}>
          <PromotionSelector
            deductionId={transaction.id}
            selectedPromotionId={transaction.promotion_id}
            onSelect={handleSelect}
            searchContextSeed={buildPromotionSearchSeedFromTransaction(transaction)}
          />
        </div>
      ) : (
        <div class="text-xs text-gray-500">From the split coding below — change it on the split.</div>
      )}
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("transaction_has_promotion_match", () => {
    it("counts a direct match named by the server", () => {
      expect(transaction_has_promotion_match({ promotion_name: "Q3 Target TPR" } as TransactionJson)).toBe(true)
      expect(transaction_has_promotion_match({ promotion_remote_id: "GP-001" } as TransactionJson)).toBe(true)
    })

    it("still counts the GoodPop-only comma-separated enrichment", () => {
      expect(transaction_has_promotion_match({ promotion_remote_ids: "GP-001, GP-002" } as TransactionJson)).toBe(true)
    })

    it("is false when nothing is matched", () => {
      expect(transaction_has_promotion_match({} as TransactionJson)).toBe(false)
      expect(transaction_has_promotion_match({ promotion_name: "" } as TransactionJson)).toBe(false)
    })
  })

  describe("should_offer_deduction_promotion_picker", () => {
    it("hides the picker when the GL coding decides the match", () => {
      expect(should_offer_deduction_promotion_picker({ promotion_match_source: "split" })).toBe(false)
    })

    it("offers it for a deduction-level match, or none at all", () => {
      expect(should_offer_deduction_promotion_picker({ promotion_match_source: "deduction" })).toBe(true)
      expect(should_offer_deduction_promotion_picker({})).toBe(true)
    })
  })
}
